import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/home/travis/build/gramps-graphql/gramps/docs/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`The `}<inlineCode parentName="p">{`@gramps/gramps`}</inlineCode>{` package exports two functions that allow `}<a parentName="p" {...{
          "href": "https://gramps.js.org/data-source/schema-stitching/"
        }}>{`GrAMPS data sources`}</a>{` to be combined.`}</p>
      <p>{`Under the hood, this is done by merging all the of data sources, applying schema stitching as needed, merging context objects, and a few other checks and chores to make it easy for developers to quickly build GraphQL servers from separate data sources.`}</p>
      <p>{`See the API docs below for the all available options, expected return values, and usage examples.`}</p>
    </PageDescription>
    <h2 {...{
      "id": "grampsoptions"
    }}><inlineCode parentName="h2">{`gramps(options)`}</inlineCode></h2>
    <p>{`The main function exported by the GrAMPS package, this generates `}<inlineCode parentName="p">{`GraphQLOptions`}</inlineCode>{` as a function that’s `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/docs/apollo-server/setup.html#options-function"
      }}>{`consumable by `}<inlineCode parentName="a">{`apollo-server`}</inlineCode></a>{`.`}</p>
    <h4 {...{
      "id": "parameters"
    }}>{`Parameters`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`gramps`}</inlineCode>{` function accepts an `}<inlineCode parentName="p">{`options`}</inlineCode>{` object which has the following properties:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p"><inlineCode parentName="strong">{`options.dataSources`}</inlineCode></strong></p>
        <p parentName="li"><em parentName="p">{`Default: `}<inlineCode parentName="em">{`[]`}</inlineCode></em></p>
        <p parentName="li">{`An array of `}<a parentName="p" {...{
            "href": "https://gramps.js.org/data-source/data-source-overview/"
          }}>{`GrAMPS data sources`}</a>{` to be combined.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p"><inlineCode parentName="strong">{`options.enableMockData`}</inlineCode></strong></p>
        <p parentName="li"><em parentName="p">{`Default: `}<inlineCode parentName="em">{`process.env.GRAMPS_MODE === 'mock'`}</inlineCode></em></p>
        <p parentName="li">{`A boolean value determining whether or not mock resolvers should be added to the schema.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p"><inlineCode parentName="strong">{`options.extraContext`}</inlineCode></strong></p>
        <p parentName="li"><em parentName="p">{`Default: `}<inlineCode parentName="em">{`req => ({})`}</inlineCode></em></p>
        <p parentName="li">{`An optional function that returns additional context to be passed to the resolvers. Must return an object.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p"><inlineCode parentName="strong">{`options.logger`}</inlineCode></strong></p>
        <p parentName="li"><em parentName="p">{`Default: `}<inlineCode parentName="em">{`console`}</inlineCode></em></p>
        <p parentName="li">{`An optional logger tool. Must expose `}<inlineCode parentName="p">{`warn`}</inlineCode>{` and `}<inlineCode parentName="p">{`error`}</inlineCode>{` methods.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p"><inlineCode parentName="strong">{`options.apollo`}</inlineCode></strong></p>
        <p parentName="li"><em parentName="p">{`Default: `}<inlineCode parentName="em">{`{}`}</inlineCode></em></p>
        <p parentName="li">{`Under the hood, GrAMPS executes the `}<inlineCode parentName="p">{`makeExecutableSchema`}</inlineCode>{` (`}<a parentName="p" {...{
            "href": "https://www.apollographql.com/docs/graphql-tools/generate-schema.html#makeExecutableSchema"
          }}>{`docs`}</a>{`) and `}<inlineCode parentName="p">{`addMockFunctionsToSchema`}</inlineCode>{` (`}<a parentName="p" {...{
            "href": "https://www.apollographql.com/docs/graphql-tools/mocking.html#addMockFunctionsToSchema"
          }}>{`docs`}</a>{`) functions from `}<inlineCode parentName="p">{`graphql-tools`}</inlineCode>{`. It also returns a configuration object for an Apollo server (`}<a parentName="p" {...{
            "href": "https://www.apollographql.com/docs/apollo-server/setup.html#graphqlOptions"
          }}>{`docs`}</a>{`). Use this option to pass additional configuration options to these function calls.`}</p>
        <p parentName="li">{`For example, to set `}<inlineCode parentName="p">{`allowUndefinedInResolve`}</inlineCode>{` to `}<inlineCode parentName="p">{`false`}</inlineCode>{` for debugging, set the option like this:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-js"
          }}>{`const GraphQLOptions = gramps({
  dataSources: [/* ... */],
  apollo: {
    makeExecutableSchema: {
      allowUndefinedInResolve: false,
    },
  },
});
`}</code></pre>
        <p parentName="li"><strong parentName="p">{`NOTE:`}</strong>{` The `}<inlineCode parentName="p">{`typeDefs`}</inlineCode>{` and `}<inlineCode parentName="p">{`resolvers`}</inlineCode>{` arguments to `}<inlineCode parentName="p">{`makeExecutableSchema`}</inlineCode>{` are set by GrAMPS, so any values set for them in the `}<inlineCode parentName="p">{`apollo`}</inlineCode>{` options will be overridden.`}</p>
      </li>
    </ul>
    <h4 {...{
      "id": "return-value"
    }}>{`Return Value`}</h4>
    <p>{`A function that accepts the request and returns a `}<inlineCode parentName="p">{`GraphQLOptions`}</inlineCode>{` object. See `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/docs/apollo-server/setup.html#options-function"
      }}>{`the apollo-server docs`}</a>{` for more information.`}</p>
    <h4 {...{
      "id": "example-of-usage"
    }}>{`Example of Usage`}</h4>
    <p>{`A simple example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import gramps from '@gramps/gramps';
import XKCD from '@gramps/data-source-xkcd';

const GraphQLOptions = gramps({ dataSources: [XKCD] });
// => \`GraphQLOptions\` is valid config for any flavor of apollo-server
`}</code></pre>
    <p>{`A complete example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import gramps from '@gramps/gramps';
import XKCD from '@gramps/data-source-xkcd';
import getPino from 'pino';

const pino = getPino({ prettyPrint: true });
pino.log = pino.info; // logger must expose a \`log\` method

const GraphQLOptions = gramps({
  dataSources: [XKCD],
  enableMockData: true,
  extraContext: req => ({
    token: req.users.token,
  }),
  logger: pino,
  apollo: {
    addMockFunctionsToSchema: {
      preserveResolvers: false,
    },
    makeExecutableSchema: {
      logger: pino,
      allowUndefinedInResolve: false,
    },
    apolloServer: {
      debug: true,
    },
  }
});
`}</code></pre>
    <h2 {...{
      "id": "prepareoptions"
    }}><inlineCode parentName="h2">{`prepare(options)`}</inlineCode></h2>
    <p>{`In some use cases, it’s better to get `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/docs/apollo-server/setup.html#options-function"
      }}><inlineCode parentName="a">{`GraphQLOptions`}</inlineCode></a>{` as an object instead of a function (for example, if you’re planning to do `}<a parentName="p" {...{
        "href": "https://gramps.js.org/data-source/schema-stitching/"
      }}>{`schema stitching`}</a>{` between your data sources).`}</p>
    <p>{`In those use cases, you can use the `}<inlineCode parentName="p">{`prepare`}</inlineCode>{` function, which is a named export from `}<inlineCode parentName="p">{`@gramps/gramps`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "parameters-1"
    }}>{`Parameters`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`prepare`}</inlineCode>{` function accepts an `}<inlineCode parentName="p">{`options`}</inlineCode>{` object that is identical to the `}<inlineCode parentName="p">{`gramps()`}</inlineCode>{` function’s.`}</p>
    <h4 {...{
      "id": "return-value-1"
    }}>{`Return Value`}</h4>
    <p>{`Returns a `}<inlineCode parentName="p">{`GraphQLOptions`}</inlineCode>{` object that can be passed directly to any Apollo server.`}</p>
    <p>{`Additionally, it exposes an `}<inlineCode parentName="p">{`addContext`}</inlineCode>{` method that follows the middleware pattern of `}<inlineCode parentName="p">{`(req, res, next) => {}`}</inlineCode>{` to allow execution of the `}<inlineCode parentName="p">{`extraContext`}</inlineCode>{` function.`}</p>
    <h4 {...{
      "id": "example-of-usage-1"
    }}>{`Example of Usage`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import Express from 'express';
import bodyParser from 'body-parser';
import { graphqlExpress, graphiqlExpress } from 'apollo-server-express';
import { mergeSchemas } from 'graphql-tools';
import { prepare } from '@gramps/gramps';
import XKCD from '@gramps/data-source-xkcd';
import Numbers from '@gramps/data-source-numbers';

const gramps = prepare({
  dataSources: [XKCD, Numbers],
  extraContext: req => ({ req }), // adds the request to the context
});

// Add a field that pulls number trivia for the given XKCD comic’s number
const linkTypeDefs = \`
  extend type XKCD_Comic {
    numbers: Numbers_Trivia
  }
\`;

// Add a resolver to make the above field actually work
const schema = mergeSchemas({
  schemas: [gramps.schema, linkTypeDefs], // use \`gramps.schema\` for merging
  resolvers: mergeInfo => ({
    XKCD_Comic: {
      numbers: {
        fragment: \`fragment XKCDFragment on XKCD_Comic { num }\`,
        resolve: (parent, args, context, info) => {
          return mergeInfo.delegate(
            'query',
            'trivia',
            { number: parent.num },
            context,
            info
          );
        }
      }
    }
  })
});

const app = new Express();

app.use('/graphql',
  bodyParser.json(),
  gramps.addContext,         // Add the extra context
  graphqlExpress({
    schema,                  // Use the merged schema...
    context: gramps.context, // ...and the GrAMPS context object
  }),
);

app.use('/graphiql', graphiqlExpress({ endpointURL: '/graphql' }));

app.listen(8080);
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      